import type { SubmitFeedbackOptions } from 'app/types/feedback';

type FeedbackUploadParams = SubmitFeedbackOptions & {
  url: string;
  body: FormData;
  headers: Record<string, string>;
};

/** The backend names the rejected attachment rule in the error body's `message`. */
function readErrorMessage(text: string): string | undefined {
  try {
    const message = (JSON.parse(text) as { message?: unknown } | null)?.message;
    return typeof message === 'string' ? message : undefined;
  } catch {
    return undefined;
  }
}

export function feedbackUploadRequest({ url, body, headers, signal, onProgress }: FeedbackUploadParams): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Feedback submission cancelled', 'AbortError'));
      return;
    }
    const request = new XMLHttpRequest();
    function cleanup(): void {
      signal?.removeEventListener('abort', abort);
    }
    function fail(error: Error): void {
      cleanup();
      reject(error);
    }
    function abort(): void {
      request.abort();
    }
    function handleAbort(): void {
      fail(new DOMException('Feedback submission cancelled', 'AbortError'));
    }
    function handleLoad(): void {
      cleanup();
      if (request.status < 200 || request.status >= 300) {
        reject(new Error(readErrorMessage(request.responseText) || `Failed to submit feedback: ${request.status}`));
      } else {
        resolve();
      }
    }
    function handleProgress(event: ProgressEvent): void {
      if (event.lengthComputable && event.total > 0) onProgress?.(Math.min(100, Math.round((event.loaded / event.total) * 100)));
    }
    request.upload.addEventListener('progress', handleProgress);
    request.addEventListener('load', handleLoad);
    request.addEventListener('error', () => fail(new Error('Feedback upload failed')));
    request.addEventListener('timeout', () => fail(new Error('Feedback upload timed out')));
    request.addEventListener('abort', handleAbort);
    request.open('POST', url);
    request.timeout = 120000;
    Object.entries(headers).forEach(([name, value]) => {
      if (name.toLowerCase() !== 'content-type') request.setRequestHeader(name, value);
    });
    signal?.addEventListener('abort', abort, { once: true });
    try {
      request.send(body);
    } catch (error) {
      cleanup();
      reject(error);
    }
  });
}
